/**
 * Kickard PHASE 4.2.0 — Weekly Cup Screen (Phase 5.0B premium V2 presentation).
 *
 * Read-only overlay on completed Ranked matches. First 15 Ranked
 * matches per player per UTC-Monday week are counted with scoring
 * 3/2/1/0 (normal-win / penalty-win / penalty-loss / normal-loss).
 *
 * • Uses server-authoritative `serverNow` + `weekEnd` for the
 *   countdown — no browser-clock drift.
 * • Historical weeks show NO live countdown.
 * • Shows real X/15 progress; "complete" only when countedMatches === 15.
 * • Never reveals hidden MMR.
 * • No gameplay/server-state effects.
 * • Header/tabs/rows reuse the Season Table V7 chrome (RankedChrome).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadGuest } from '@/lib/guestIdentity';
import { usePlayerRowLinks } from '@/lib/playerProfileLink';
import { fetchWeeklyCup } from '@/api/weeklyCupClient';
import {
  RankedBackdrop, RankedHeader, RankedTabs, SEASON_TABLE_ASSETS,
} from '../components/RankedChrome';

const REFRESH_MS = 15_000;
const MAX_COUNTED = 15;
const W = '/kickard/weekly-cup';

export function fmtCountdown(msRemaining) {
  if (msRemaining <= 0) return '00d 00h 00m 00s';
  const s = Math.floor(msRemaining / 1000);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(days)}d ${pad(hours)}h ${pad(mins)}m ${pad(secs)}s`;
}

export function progressPct(counted) {
  const n = Math.max(0, Math.min(MAX_COUNTED, Number(counted) || 0));
  return (n / MAX_COUNTED) * 100;
}

const ClockIcon = () => (
  <svg viewBox="0 0 64 64" aria-hidden><circle cx="32" cy="32" r="23" fill="none" stroke="currentColor" strokeWidth="4" /><path d="M32 18v15l10 7" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" /></svg>
);
const StarIcon = () => (
  <svg viewBox="0 0 64 64" aria-hidden><path d="m32 6 8 17 18 2-13 13 3 19-16-9-16 9 3-19L6 25l18-2Z" fill="#F3B82A" /></svg>
);
const RankBarsIcon = () => (
  <svg viewBox="0 0 64 64" aria-hidden><rect x="8" y="36" width="12" height="20" rx="1.5" fill="#F3B82A" /><rect x="26" y="22" width="12" height="34" rx="1.5" fill="#F3B82A" /><rect x="44" y="8" width="12" height="48" rx="1.5" fill="#F3B82A" /></svg>
);
const TrendIcon = () => (
  <svg viewBox="0 0 64 64" aria-hidden><path d="M12 50h40M16 44l10-11 9 6 14-17M42 22h7v7" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

const SCORING = [['Win', '+3 PTS'], ['Penalty win', '+2 PTS'], ['Penalty loss', '+1 PT'], ['Loss', '+0 PTS']];

function WeeklyHero({ data, msRemaining }) {
  const current = !!data.isCurrentWeek;
  return (
    <section className="kwc-hero" data-testid="weekly-cup-hero">
      <div className="kwc-hero__bg" aria-hidden />
      <div className="kwc-hero__top">
        <img src={`${W}/weekly_cup_trophy.webp`} alt="" aria-hidden className="kwc-hero__trophy" />
        <div className="kwc-hero__text">
          <div className="kwc-eyebrow" data-testid="weekly-cup-week-label">{current ? 'This Week' : 'Historical Week'}</div>
          <h2 className="kwc-title"><span className="kwc-title__gold">WEEKLY</span><span className="kwc-title__white">CUP</span></h2>
          <div className="kwc-tagline" data-testid="weekly-cup-tagline">Compete. Score. Climb the table.</div>
        </div>
        <div className="kwc-countdown">
          <ClockIcon />
          {current && msRemaining !== null ? (
            <div>
              <div className="kwc-countdown__label">Ends in</div>
              <div className="kwc-countdown__value" data-testid="weekly-cup-countdown">{fmtCountdown(msRemaining)}</div>
            </div>
          ) : (
            <div>
              <div className="kwc-countdown__label">Week of</div>
              <div className="kwc-countdown__value" data-testid="weekly-cup-week-of">
                {current ? '—' : new Date(data.weekStart).toISOString().slice(0, 10)}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="kwc-scoring" data-testid="weekly-cup-scoring">
        {SCORING.map(([label, value]) => (
          <div className="kwc-scoring__cell" key={label}>
            <div className="kwc-scoring__label">{label}</div>
            <div className="kwc-scoring__value">{value}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ViewerStat({ icon, label, value, testid }) {
  return (
    <div className="kwc-stat">
      {icon}
      <div style={{ minWidth: 0 }}>
        <div className="kwc-stat__label">{label}</div>
        <div className="kwc-stat__value" data-testid={testid}>{value}</div>
      </div>
    </div>
  );
}

export function WeeklyProgress({ viewerRow, isCurrentWeek }) {
  const counted = viewerRow ? Math.min(MAX_COUNTED, Number(viewerRow.countedMatches) || 0) : 0;
  const done = !!viewerRow && viewerRow.countedMatches === MAX_COUNTED;
  return (
    <section className="kwc-progress" data-testid={viewerRow ? 'weekly-cup-viewer-card' : 'weekly-cup-viewer-empty'}>
      <div className="kwc-progress__top">
        <div className="kwc-progress__head">
          <div className="kwc-progress__title">Your Progress</div>
          <div className="kwc-progress__count">
            <b data-testid="weekly-cup-viewer-matches">{counted}</b>/ {MAX_COUNTED} Matches
            {done && <span className="kwc-progress__complete" data-testid="weekly-cup-complete-msg">· Complete</span>}
          </div>
        </div>
        <div className="kwc-bar-row">
          <div className="kwc-bar" role="progressbar" aria-valuemin={0} aria-valuemax={MAX_COUNTED} aria-valuenow={counted} data-testid="weekly-cup-progress">
            <div className="kwc-bar__fill" style={{ width: `${progressPct(counted)}%` }} data-testid="weekly-cup-progress-fill" />
          </div>
          <img
            src={`${W}/progress_ball_laurel.webp`}
            alt=""
            aria-hidden
            draggable={false}
            className={`kwc-marker${done ? ' kwc-marker--done' : ''}`}
            data-testid="weekly-cup-progress-marker"
          />
        </div>
        <div className="kwc-bar-labels"><span>{counted}</span><span>{MAX_COUNTED}</span></div>
      </div>
      {!viewerRow && (
        <div className="kwc-empty-note">
          {isCurrentWeek
            ? 'Play a Ranked match to appear on this week\'s cup.'
            : 'You did not play a Ranked match in this week.'}
        </div>
      )}
      <div className="kwc-stats">
        <ViewerStat icon={<StarIcon />} label="Your points" value={viewerRow ? viewerRow.weeklyPoints : '—'} testid="weekly-cup-viewer-points" />
        <ViewerStat icon={<RankBarsIcon />} label="Your rank" value={viewerRow ? `#${viewerRow.rank}` : '—'} testid="weekly-cup-viewer-rank" />
        <ViewerStat icon={<TrendIcon />} label="W-L this week" value={viewerRow ? `${viewerRow.wins}-${viewerRow.losses}` : '—'} testid="weekly-cup-viewer-wl" />
      </div>
    </section>
  );
}

function gdClass(gd) {
  return gd > 0 ? 'kwc-gd--pos' : gd < 0 ? 'kwc-gd--neg' : 'kwc-gd--zero';
}

export function WeeklyRow({ row, isMe }) {
  const top = row.rank >= 1 && row.rank <= 3;
  const skin = top ? `kst__row--rank${row.rank}` : (isMe ? 'kst__row--selected' : '');
  const gd = Number(row.goalDifference) || 0;
  return (
    <div className={`kst__row kwc-row ${skin}`} data-testid={`weekly-cup-row-${row.rank}`} data-guestid={row.guestId} data-me={isMe ? 'true' : 'false'} role="button" tabIndex={0} aria-label={`Open ${row.displayName || 'Player'} profile`}>
      <div className="kst__cell kwc-c-rank">
        {top
          ? <img src={`${SEASON_TABLE_ASSETS}/medal_${row.rank}.webp`} alt={`Rank ${row.rank}`} className="kst__medal" />
          : <span className="kst__rank-num">{row.rank}</span>}
      </div>
      <div className="kst__cell kwc-c-player">
        <div className="kst__name-line">
          <span className="kst__name">{row.displayName}</span>
          {isMe && <span className="kst-you" data-testid="weekly-cup-you-chip">YOU</span>}
        </div>
      </div>
      <div className="kst__cell kwc-c-pts"><span className="kwc-pts">{row.weeklyPoints}</span></div>
      <div className="kst__cell kwc-c-m"><span className="kwc-num">{row.countedMatches}/{MAX_COUNTED}</span></div>
      <div className="kst__cell kwc-c-wl"><span className="kwc-num">{row.wins}-{row.losses}</span></div>
      <div className="kst__cell kwc-c-gd">
        <span className={`kwc-gd ${gdClass(gd)}`}>{gd > 0 ? '+' : ''}{gd}</span>
      </div>
    </div>
  );
}

export function WeeklyBoard({ rows, guestId }) {
  return (
    <section className="kwc-board" data-testid="weekly-cup-leaderboard">
      <div className="kwc-board__head">
        <span className="kwc-c-rank">#</span>
        <span className="kwc-c-player">Player</span>
        <span className="kwc-c-pts">Pts</span>
        <span className="kwc-c-m">M</span>
        <span className="kwc-c-wl">W-L</span>
        <span className="kwc-c-gd">GD</span>
      </div>
      {rows.length === 0 ? (
        <div className="kwc-board__empty">No matches counted this week yet.</div>
      ) : (
        <div className="kwc-rows">
          {rows.map((row) => (
            <WeeklyRow key={`${row.rank}-${row.guestId}`} row={row} isMe={!!guestId && row.guestId === guestId} />
          ))}
        </div>
      )}
    </section>
  );
}

export function WeeklyCupView({ data, loading, err, msRemaining, guestId, onBack, onNavigate }) {
  const viewerRow = data?.viewerRow || (data?.leaderboard || []).find(
    (r) => guestId && r.guestId === guestId
  );
  return (
    <RankedBackdrop testid="weekly-cup-screen">
      <div className="kst__inner kwc" style={{ '--kwc-stadium': `url(${W}/weekly_stadium.webp)` }}>
        <RankedHeader
          title="WEEKLY CUP"
          subtitle="RANKED WEEKLY TOURNAMENT"
          onBack={onBack}
          backTestId="weekly-cup-back"
          titleTestId="weekly-cup-title"
        />
        <RankedTabs active="weekly-cup" onNavigate={onNavigate} />

        {loading && <div className="kwc-state" data-testid="weekly-cup-loading">Loading…</div>}
        {err && !loading && (
          <div className="kwc-state kwc-state--err" data-testid="weekly-cup-error">
            Couldn't load Weekly Cup ({err}).
          </div>
        )}
        {data && (
          <>
            <WeeklyHero data={data} msRemaining={msRemaining} />
            <WeeklyProgress viewerRow={viewerRow} isCurrentWeek={data.isCurrentWeek} />
            <WeeklyBoard rows={data.leaderboard || []} guestId={guestId} />
          </>
        )}
      </div>
    </RankedBackdrop>
  );
}

export default function WeeklyCupScreen() {
  const navigate = useNavigate();
  usePlayerRowLinks(navigate);
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const guest = useMemo(() => loadGuest(), []);

  // Origin-aware Back — same pattern as Ranked leaderboard. When this
  // screen was opened from a finalized Match Result, return to the
  // SAME Match Result via `/match-summary/:matchId`. Otherwise keep
  // the existing browser-history Back behavior intact.
  const handleBack = () => {
    const origin = location.state?.origin;
    const returnPath = location.state?.returnPath;
    if (origin === 'match_result' && typeof returnPath === 'string' && returnPath) {
      navigate(returnPath, { replace: true });
      return;
    }
    navigate(-1);
  };
  // Compute the browser-tick-only "now" strictly relative to serverNow
  // when the API was called — keeps the countdown accurate without
  // trusting the local clock.
  const [nowLocalMs, setNowLocalMs] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const res = await fetchWeeklyCup({ guestId: guest?.guestId });
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) { setErr(res.code || 'error'); return; }
      setData({
        ...res,
        // Capture the wall-clock at API response so we can compute a
        // drift-tolerant countdown against `res.serverNow` + `res.weekEnd`.
        _apiLocalMs: Date.now(),
      });
    }
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [guest?.guestId]);

  useEffect(() => {
    const tick = setInterval(() => setNowLocalMs(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  const msRemaining = useMemo(() => {
    if (!data || !data.isCurrentWeek) return null;
    try {
      const endMs = new Date(data.weekEnd).getTime();
      const serverNowMs = new Date(data.serverNow).getTime();
      // Rebase server clock to local ticking clock via captured offset.
      const local = nowLocalMs - (data._apiLocalMs - serverNowMs);
      return Math.max(0, endMs - local);
    } catch (_) { return null; }
  }, [data, nowLocalMs]);

  return (
    <WeeklyCupView
      data={data}
      loading={loading}
      err={err}
      msRemaining={msRemaining}
      guestId={guest?.guestId}
      onBack={handleBack}
      onNavigate={(path) => navigate(path)}
    />
  );
}
